import React, { useState } from "react";
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MaterialButton from '@mui/material/Button';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getDoorSign } from "../../services/UserService";
import formatDate from "../../utils/formatDate";
import winterBackground from '../../assets/Bakgrunn_aktive-deg_winter.png';
import summerBackground from '../../assets/Bakgrunn_aktive-deg_summer.png';

export default function UserInfoDrawer(props) {
  const [photo, setPhoto] = useState()
  const user = props.user
  const isWinter = user.isWinter !== false;
  const seasonAccent = isWinter ? '#439AC2' : '#7FAF84';
  const seasonAccentHover = isWinter ? '#2F86AD' : '#6E9672';
  const seasonOutline = isWinter ? '#9FC8DA' : '#B6D0B8';
  const seasonBackground = isWinter ? winterBackground : summerBackground;

  function createData(name, response) {
    return { name, response };
  }

  const toText = (value) => {
    switch (Number(value)) {
      case 1:
        return 'Ikke gøy'
      case 2:
        return 'Skummelt'
      case 3:
        return 'Litt gøy'
      case 4:
        return 'Veldig gøy'
      case 5:
        return 'Vet ikke'
      default:
        return ''
    }
  }


  const getPhoto = async () => {
    const result = await getDoorSign(user.userId)

    if (result && result.ok && result.url) {
      window.open(result.url, '_blank', 'noreferrer')
      return
    }

    setPhoto(1)
  }

  function ShowPhoto() {
    if (photo === 1) {
      return (
        <Box sx={{ width: '22%' }}>
          <Typography>Ikke generert dørskilt</Typography>
        </Box>
      )
    } else {
      return null
    }
  }


  const toggleDrawer = () => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    props.setOpen(false)
  };

  const rows = [
    createData('Ri på hest', toText(user.horse)),
    createData('Klatring', toText(user.climbing)),
    createData('Basseng', toText(user.pool)),
    createData('Gymsal', toText(user.gym)),
    createData('Være på tur', toText(user.hike)),
    createData('Friluftsliv', toText(user.outdoor)),
    createData('Alpin', toText(user.alpin)),
    createData('Skøyter', toText(user.skates)),
    createData('Langrenn', toText(user.skiing)),
    createData('Aktivitet skjerm', toText(user.screen)),
    createData('Leke ute', toText(user.playOutside)),
    createData('Bevegelse musikk', toText(user.musicMove)),
    createData('Utendørs vann', toText(user.canoe)),
    createData('Sykle', toText(user.cycle)),
  ];
  const answeredRows = rows.filter((row) => row.response);
  const responseColors = {
    'Ikke gøy': { bgcolor: '#FDECEC', color: '#D75B6C' },
    'Skummelt': { bgcolor: '#FCE7D9', color: '#C96A2D' },
    'Litt gøy': { bgcolor: '#EDF3FA', color: '#4B6F95' },
    'Veldig gøy': { bgcolor: '#EAF7E3', color: '#3F7F4C' },
    'Vet ikke': { bgcolor: '#FFF4D6', color: '#B7791F' },
  };
  const initials = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';

  return (
    <Drawer
      open={props.open}
      anchor={"right"}
      onClose={toggleDrawer()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          backgroundImage: `url(${seasonBackground})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(253, 254, 255, 0.25)',
            pointerEvents: 'none',
          },
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{ minHeight: '100%', position: 'relative', zIndex: 1, bgcolor: 'transparent', padding: { xs: 2, sm: 4 }, color: '#102A56' }}>
        <IconButton
          onClick={toggleDrawer()}
          aria-label="Lukk pasientinformasjon"
          sx={{ position: 'absolute', top: 16, right: 16, color: '#102A56' }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: '12px 40px 16px 0' }}>
          <Avatar sx={{ width: 84, height: 84, bgcolor: isWinter ? '#DDF2FA' : '#E5F2E3', color: seasonAccentHover, fontSize: 36, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ color: '#102A56', fontSize: 24, lineHeight: 1.15, fontWeight: 700 }}>
              {user.firstName}
            </Typography>
            <Typography sx={{ color: seasonAccentHover, fontSize: 14, fontWeight: 600, marginTop: 0.5 }}>
              PasientID: {user.userId}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', bgcolor: 'rgba(248, 252, 254, 0.86)', border: `2px solid ${seasonOutline}`, borderRadius: '14px', padding: 2, marginBottom: 2, backdropFilter: 'blur(8px)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, borderRight: '1px solid #CBD5E1', paddingRight: 2 }}>
            <CalendarMonthIcon sx={{ color: seasonAccent, fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: '#64748B', fontSize: 12 }}>Ankomst</Typography>
              <Typography sx={{ color: '#102A56', fontSize: 14, fontWeight: 700 }}>{formatDate(user.arrivalDate)}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, paddingLeft: 2 }}>
            <CalendarMonthIcon sx={{ color: seasonAccent, fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: '#64748B', fontSize: 12 }}>Avreise</Typography>
              <Typography sx={{ color: '#102A56', fontSize: 14, fontWeight: 700 }}>{user.departureDate ? formatDate(user.departureDate) : '-'}</Typography>
            </Box>
          </Box>
        </Box>

        <MaterialButton
          onClick={getPhoto}
          startIcon={<BadgeOutlinedIcon />}
          fullWidth
          sx={{ minHeight: 48, border: `2px solid ${seasonOutline}`, borderRadius: '14px', color: seasonAccentHover, bgcolor: 'rgba(255, 255, 255, 0.76)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', marginBottom: 1.5, '&:hover': { borderColor: seasonAccent, bgcolor: 'rgba(255, 255, 255, 0.9)' } }}
        >
          Hent dørskilt
        </MaterialButton>
        <ShowPhoto />

        <Box sx={{ border: `2px solid ${seasonOutline}`, borderRadius: '14px', padding: 1.5, marginTop: 3, bgcolor: 'rgba(248, 252, 254, 0.78)', backdropFilter: 'blur(8px)' }}>
          <Typography sx={{ color: seasonAccentHover, fontSize: 16, fontWeight: 700, marginBottom: 1.25 }}>
            <Box component="span" sx={{ display: 'block', textAlign: 'center' }}>Aktive deg</Box>
          </Typography>
          <TableContainer sx={{ overflow: 'hidden' }}>
            <Table sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, borderRadius: '10px', overflow: 'hidden' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: isWinter ? '#E5F5FA' : '#E8F3E8' }}>
                  <TableCell sx={{ width: '58%', bgcolor: isWinter ? '#E5F5FA' : '#E8F3E8', color: seasonAccentHover, fontSize: 13, fontWeight: 700, borderBottom: `1px solid ${seasonOutline}`, padding: '12px 16px' }}>Aktivitet</TableCell>
                  <TableCell sx={{ bgcolor: isWinter ? '#E5F5FA' : '#E8F3E8', color: seasonAccentHover, fontSize: 13, fontWeight: 700, borderBottom: `1px solid ${seasonOutline}`, padding: '12px 16px' }}>Svar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {answeredRows.map((row, index) => {
                  const colors = responseColors[row.response] || { bgcolor: '#F1F5F9', color: '#334155' };
                  return (
                    <TableRow key={row.name} sx={{ bgcolor: index % 2 ? 'rgba(248, 252, 254, 0.72)' : 'rgba(255, 255, 255, 0.72)' }}>
                      <TableCell sx={{ color: '#102A56', fontSize: 13, borderBottom: `1px solid ${seasonOutline}`, padding: '10px 16px' }}>{row.name}</TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${seasonOutline}`, padding: '8px 16px' }}>
                        <Chip label={row.response} sx={{ bgcolor: colors.bgcolor, color: colors.color, height: 30, fontSize: 13, fontWeight: 600, borderRadius: '999px' }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Drawer>
  )

}